import { UserManager, WebStorageStateStore } from "oidc-client-ts";

/**
 * IRWMS single sign-on for the WMS frontend.
 *
 * WMS is a public OIDC client (`wms-spa`, authorization-code + PKCE) of the
 * IRWMS Keycloak realm. The Keycloak access token is used exactly once — to
 * exchange for a normal WMS session at POST /auth/sso — and is not kept; from
 * then on the app runs on its own access token + refresh cookie as before.
 *
 * Enabled only when VITE_OIDC_ISSUER is set; otherwise the local PF-number
 * login is the only path (dev without Keycloak, or an emergency fallback).
 */

const ISSUER = import.meta.env.VITE_OIDC_ISSUER?.trim();
const CLIENT_ID = import.meta.env.VITE_OIDC_CLIENT_ID?.trim() || "wms-spa";
const PORTAL_URL = import.meta.env.VITE_IRWMS_PORTAL_URL?.trim();

export const SSO_ENABLED = Boolean(ISSUER);
/**
 * True only when this page load was opened from the IRWMS portal (its Open
 * button appends ?sso=1). Then the app goes straight to the identity provider;
 * a direct visit shows the app's own login form, with SSO as a button.
 */
export const SSO_REQUESTED =
  SSO_ENABLED && new URLSearchParams(window.location.search).get("sso") === "1";
export const SSO_CALLBACK_PATH = "/oidc/callback";
/** Where "Back to portal" and post-logout go. */
export const IRWMS_PORTAL_URL = PORTAL_URL || (ISSUER ? new URL(ISSUER).origin : undefined);

const RETURN_TO_KEY = "wms_sso_return_to";

let manager: UserManager | null = null;

function userManager(): UserManager {
  if (!ISSUER) {
    throw new Error("IRWMS SSO is not configured (VITE_OIDC_ISSUER)");
  }
  manager ??= new UserManager({
    authority: ISSUER,
    client_id: CLIENT_ID,
    redirect_uri: `${window.location.origin}${SSO_CALLBACK_PATH}`,
    post_logout_redirect_uri: IRWMS_PORTAL_URL ?? window.location.origin,
    response_type: "code",
    scope: "openid profile email",
    // One-shot exchange: no renewal, no session monitoring, nothing persisted.
    automaticSilentRenew: false,
    monitorSession: false,
    loadUserInfo: false,
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  });
  return manager;
}

/** Send the browser to Keycloak; comes back to /oidc/callback with a code. */
export async function beginSso(returnTo: string): Promise<void> {
  const safe = returnTo.startsWith("/") && !returnTo.startsWith("//") ? returnTo : "/";
  try {
    sessionStorage.setItem(RETURN_TO_KEY, safe);
  } catch {
    /* storage unavailable: land on the dashboard instead */
  }
  await userManager().signinRedirect();
}

/**
 * Complete the code exchange with Keycloak and hand back the access token for
 * the WMS backend to verify. The token is removed from the OIDC store at once.
 */
export async function completeSso(): Promise<{
  accessToken: string;
  idToken?: string;
  returnTo: string;
}> {
  const um = userManager();
  const user = await um.signinCallback();
  if (!user?.access_token) {
    throw new Error("Sign-on did not return a token");
  }
  let returnTo = "/";
  try {
    returnTo = sessionStorage.getItem(RETURN_TO_KEY) ?? "/";
    sessionStorage.removeItem(RETURN_TO_KEY);
  } catch {
    /* ignore */
  }
  // Keep only the id_token hint for logout; the access token must not linger.
  try {
    sessionStorage.setItem("wms_sso_id_token", user.id_token ?? "");
  } catch {
    /* ignore */
  }
  await um.removeUser();
  return { accessToken: user.access_token, idToken: user.id_token, returnTo };
}

/**
 * End the Keycloak session too (front-channel logout reaches every other app)
 * and land on the portal. Call after the local WMS session is cleared.
 */
export function logoutViaSso(): void {
  if (!ISSUER) {
    return;
  }
  let idToken: string | null = null;
  try {
    idToken = sessionStorage.getItem("wms_sso_id_token");
    sessionStorage.removeItem("wms_sso_id_token");
  } catch {
    /* ignore */
  }
  const params = new URLSearchParams({
    post_logout_redirect_uri: IRWMS_PORTAL_URL ?? window.location.origin,
  });
  if (idToken) {
    params.set("id_token_hint", idToken);
  }
  window.location.assign(`${ISSUER}/protocol/openid-connect/logout?${params.toString()}`);
}

/** Whether the current local session was opened through IRWMS. */
export function sessionCameFromSso(): boolean {
  try {
    return sessionStorage.getItem("wms_sso_id_token") !== null;
  } catch {
    return false;
  }
}
